import React, { useEffect, useRef, useState } from 'react';
import cytoscape from 'cytoscape';
import { walletApi } from '../services/api';

interface TransactionFlowProps {
    signature: string;
    onClose: () => void;
}

interface FlowNode {
    from: string;
    to: string;
    amount: number;
    signature: string;
    timestamp: Date;
    hasMore: boolean;
}

// Add type for the API response
interface FlowResponse {
    flow: FlowNode[];
    depth: number;
    hasMore?: boolean;
}

const TransactionFlow: React.FC<TransactionFlowProps> = ({ signature, onClose }) => {
    const [flow, setFlow] = useState<FlowNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandingNode, setExpandingNode] = useState<string | null>(null);
    const [initialWallet, setInitialWallet] = useState<string | null>(null);
    const [edgeLoading, setEdgeLoading] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const cyRef = useRef<cytoscape.Core | null>(null);
    const graphPositionRef = useRef<{ zoom: number; pan: { x: number; y: number } } | null>(null);

    const fetchFlow = async (sig: string) => {
        try {
            setLoading(true);
            const response: FlowResponse = await walletApi.getTransactionFlow(sig);
            console.log('Transaction Flow Data:', response.flow);
            setFlow(response.flow);
            // Set initial wallet from the first transaction
            if (response.flow.length > 0) {
                setInitialWallet(response.flow[0].from);
            }
        } catch (err) {
            setError('Failed to fetch transaction flow');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchNextFlow = async (walletAddress: string) => {
        try {
            setExpandingNode(walletAddress);
            const response: FlowResponse = await walletApi.getTransactionFlow(walletAddress);
            console.log('Next Flow Data:', response.flow);
            
            // Merge new flow with existing flow, avoiding duplicates
            const newFlow = [...flow];
            response.flow.forEach((newNode: FlowNode) => {
                const isDuplicate = newFlow.some(
                    existing => existing.signature === newNode.signature
                );
                if (!isDuplicate) {
                    newFlow.push(newNode);
                }
            });
            
            setFlow(newFlow);
        } catch (err) {
            setError('Failed to fetch next transaction flow');
            console.error(err);
        } finally {
            setExpandingNode(null);
        }
    };

    useEffect(() => {
        fetchFlow(signature);
    }, [signature]);

    useEffect(() => {
        if (!containerRef.current || !flow.length) return;

        // Save current position before re-render
        if (cyRef.current) {
            graphPositionRef.current = {
                zoom: cyRef.current.zoom(),
                pan: cyRef.current.pan()
            };
        }

        console.log('Initializing graph with flow:', flow);

        // Initialize Cytoscape
        const cy = cytoscape({
            container: containerRef.current,
            style: [
                {
                    selector: 'node',
                    style: {
                        'background-color': '#4A90E2',
                        'label': 'data(label)',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'text-wrap': 'wrap',
                        'text-max-width': '60px',
                        'font-size': '10px',
                        'color': '#fff',
                        'text-outline-width': 1,
                        'text-outline-color': '#000',
                        'width': 30,
                        'height': 30
                    }
                },
                {
                    selector: 'node.expandable',
                    style: {
                        'background-color': '#FFA500',
                        'border-width': 2,
                        'border-color': '#FF8C00'
                    }
                },
                {
                    selector: 'node.expanding',
                    style: {
                        'background-color': '#FFD700',
                        'border-width': 3,
                        'border-color': '#FFA500'
                    }
                },
                {
                    selector: 'node.initial',
                    style: {
                        'background-color': '#4A90E2',
                        'border-width': 3,
                        'border-color': '#2C5282',
                        'z-index': 999
                    }
                },
                {
                    selector: 'edge',
                    style: {
                        'width': 1,
                        'line-color': '#999',
                        'target-arrow-color': '#999',
                        'target-arrow-shape': 'triangle',
                        'curve-style': 'bezier',
                        'label': 'data(label)',
                        'font-size': '8px',
                        'text-rotation': 'autorotate',
                        'text-margin-y': -8,
                        'text-background-color': '#fff',
                        'text-background-opacity': 0.8,
                        'text-background-padding': '2px'
                    }
                },
                {
                    selector: 'edge.loading',
                    style: {
                        'line-color': '#FFA500',
                        'target-arrow-color': '#FFA500',
                        'width': 2
                    }
                }
            ],
            layout: {
                name: 'preset',
                fit: false,
                padding: 30
            }
        });

        // Add nodes and edges
        const nodes = new Set<string>();
        flow.forEach(node => {
            nodes.add(node.from);
            nodes.add(node.to);
        });

        console.log('Unique nodes to add:', Array.from(nodes));

        // Store existing node positions
        const existingPositions = new Map();
        if (cyRef.current) {
            cyRef.current.nodes().forEach(node => {
                existingPositions.set(node.id(), {
                    x: node.position('x'),
                    y: node.position('y')
                });
            });
        }

        nodes.forEach(address => {
            const node = cy.add({
                group: 'nodes',
                data: {
                    id: address,
                    label: `${address.slice(0, 6)}...${address.slice(-4)}`,
                    hasMore: flow.some(n => (n.from === address || n.to === address) && n.hasMore)
                }
            });

            // Restore position if it exists
            if (existingPositions.has(address)) {
                const pos = existingPositions.get(address);
                node.position(pos);
            } else {
                // Only apply random position to new nodes
                node.position({
                    x: Math.random() * 500,
                    y: Math.random() * 500
                });
            }

            // Add expandable class if node has more transactions and is not the initial wallet
            if (node.data('hasMore') && address !== initialWallet) {
                node.addClass('expandable');
            }

            // Add expanding class if node is currently being expanded
            if (address === expandingNode) {
                node.addClass('expanding');
            }

            // Initial wallet is always blue
            if (address === initialWallet) {
                node.addClass('initial');
            }
        });

        flow.forEach((node, index) => {
            console.log(`Adding edge ${index}:`, {
                from: node.from,
                to: node.to,
                amount: node.amount
            });
            cy.add({
                group: 'edges',
                data: {
                    id: `edge-${index}`,
                    source: node.from,
                    target: node.to,
                    label: `${node.amount.toFixed(2)} KOII`,
                    signature: node.signature,
                    timestamp: node.timestamp
                }
            });
        });

        // Add event listeners
        cy.on('tap', 'node', (evt) => {
            const node = evt.target;
            const address = node.id();
            
            if (node.hasClass('expandable') && !node.hasClass('expanding')) {
                fetchNextFlow(address);
            } else {
                // Show full address in a tooltip or modal
                alert(`Full Address: ${address}`);
            }
        });

        cy.on('tap', 'edge', async (evt) => {
            const edge = evt.target;
            const signature = edge.data('signature');
            
            // Add loading state to the edge
            edge.addClass('loading');
            setEdgeLoading(signature);

            try {
                // Fetch transaction details
                const response = await walletApi.getTransactionFlow(signature);
                const timestamp = new Date(edge.data('timestamp')).toLocaleString();
                
                // Show transaction details
                alert(`Transaction ID: ${signature}\nTimestamp: ${timestamp}`);
                
                // Update flow with new transactions
                const newFlow = [...flow];
                response.flow.forEach((newNode: FlowNode) => {
                    const isDuplicate = newFlow.some(
                        existing => existing.signature === newNode.signature
                    );
                    if (!isDuplicate) {
                        newFlow.push(newNode);
                    }
                });
                setFlow(newFlow);
            } catch (err) {
                console.error('Error fetching transaction details:', err);
                alert('Failed to fetch transaction details');
            } finally {
                // Remove loading state
                edge.removeClass('loading');
                setEdgeLoading(null);
            }
        });

        // Add right-click handler for nodes
        cy.on('cxttap', 'node', (evt) => {
            const node = evt.target;
            const address = node.id();
            alert(`Node Address: ${address}`);
        });

        // Add right-click handler for edges
        cy.on('cxttap', 'edge', (evt) => {
            const edge = evt.target;
            const transactionId = edge.data('signature');
            // goes to explorer
            window.open(`https://explorer.koii.network/transaction/${transactionId}`, '_blank');
        });

        // Restore position after initialization
        if (graphPositionRef.current) {
            cy.zoom(graphPositionRef.current.zoom);
            cy.pan(graphPositionRef.current.pan);
        } else {
            // Only fit on first load
            cy.fit();
        }

        // Add zoom controls
        cy.on('zoom', () => {
            const zoom = cy.zoom();
            if (zoom < 0.5) {
                cy.zoom(0.5);
            } else if (zoom > 2) {
                cy.zoom(2);
            }
        });

        // Add zoom buttons
        const zoomIn = document.createElement('button');
        zoomIn.className = 'absolute top-2 right-2 bg-white p-1 rounded shadow hover:bg-gray-100';
        zoomIn.innerHTML = '+';
        zoomIn.onclick = () => cy.zoom(cy.zoom() * 1.2);
        containerRef.current?.appendChild(zoomIn);

        const zoomOut = document.createElement('button');
        zoomOut.className = 'absolute top-2 right-10 bg-white p-1 rounded shadow hover:bg-gray-100';
        zoomOut.innerHTML = '-';
        zoomOut.onclick = () => cy.zoom(cy.zoom() / 1.2);
        containerRef.current?.appendChild(zoomOut);

        const resetZoom = document.createElement('button');
        resetZoom.className = 'absolute top-2 right-20 bg-white p-1 rounded shadow hover:bg-gray-100';
        resetZoom.innerHTML = '⟲';
        resetZoom.onclick = () => {
            cy.fit();
            cy.center();
        };
        containerRef.current?.appendChild(resetZoom);

        cyRef.current = cy;

        // Cleanup zoom buttons
        return () => {
            zoomIn.remove();
            zoomOut.remove();
            resetZoom.remove();
            cy.destroy();
        };
    }, [flow, expandingNode, initialWallet, edgeLoading]);

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg shadow-xl">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                    <p className="mt-4 text-gray-600">Loading transaction flow...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg shadow-xl">
                    <p className="text-red-500">{error}</p>
                    <button
                        onClick={onClose}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white p-4 rounded-lg shadow-xl w-full max-w-4xl h-[70vh] flex flex-col">
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-lg font-semibold">Transaction Flow</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>
                
                <div className="flex-1 min-h-0 border border-gray-200 rounded-lg relative">
                    <div ref={containerRef} className="w-full h-full" />
                    {edgeLoading && (
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                        </div>
                    )}
                </div>

                <div className="mt-2 text-xs text-gray-500">
                    <p>Click on orange nodes to expand the flow</p>
                    <p>Click on blue nodes to see full wallet addresses</p>
                    <p>Click on edges to see transaction details</p>
                    <p>Right-click on edges to see target wallet address</p>
                </div>
            </div>
        </div>
    );
};

export default TransactionFlow; 